// Supabase-backed storage for the Precon app.
//
// The app was written against a document store: collection(name).doc(id).get/set/update/delete,
// collection.add and collection.onSnapshot, with nested objects merging on update. This module
// keeps that interface and translates each collection to the relational precon_* tables, so the
// tested app code runs unchanged. Security is enforced by the database's row level security.

export function roleSlug(label) {
  return String(label || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
const rand = () => Math.random().toString(36).slice(2, 8);
const num = v => (v === null || v === undefined || v === "" ? null : Number(v));

// Database errors become the { code, message } shape the app already understands.
export function toAppError(error) {
  const msg = (error && error.message) || String(error);
  const denied = error && (error.code === "42501" || /row-level security|permission|not allowed|can't|cannot/i.test(msg));
  const invalid = error && /^(22|23)/.test(String(error.code || ""));
  return { code: denied || invalid ? "invalid_argument" : "upstream_error", message: msg };
}
function check(res) {
  if (res && res.error) throw toAppError(res.error);
  return res ? res.data : null;
}
function inList(ids) { return "(" + ids.map(x => '"' + String(x).replace(/"/g, '\\"') + '"').join(",") + ")"; }

export function createStore(sb) {
  const roles = { list: [], loaded: false };
  async function loadRoles() {
    roles.list = check(await sb.from("precon_roles").select("id,label,sort").order("sort")) || [];
    roles.loaded = true;
    return roles.list;
  }
  async function roleIdFor(label) {
    if (!roles.loaded) await loadRoles();
    let r = roles.list.find(x => x.label.toLowerCase() === String(label || "").toLowerCase());
    if (!r) { await loadRoles(); r = roles.list.find(x => x.label.toLowerCase() === String(label || "").toLowerCase()); }
    return r ? r.id : null;
  }
  const roleLabel = id => (roles.list.find(r => r.id === id) || {}).label || "";
  async function roleIdForSlug(slug) {
    if (!roles.loaded) await loadRoles();
    let r = roles.list.find(x => roleSlug(x.label) === slug);
    if (!r) { await loadRoles(); r = roles.list.find(x => roleSlug(x.label) === slug); }
    return r ? r.id : null;
  }

  // ---------------------------------------------------------------- per-collection translators
  const handlers = {};

  handlers.roles = {
    tables: ["precon_roles"],
    async list() { return (await loadRoles()).map(r => ({ id: r.id, data: { label: r.label, order: r.sort } })); },
    async set(id, d) { check(await sb.from("precon_roles").upsert({ id, label: d.label, sort: d.order || 0 })); await loadRoles(); },
    async update(id, p) {
      const row = {};
      if ("label" in p) row.label = p.label;
      if ("order" in p) row.sort = p.order;
      if (Object.keys(row).length) check(await sb.from("precon_roles").update(row).eq("id", id));
      await loadRoles();
    },
    async remove(id) { check(await sb.from("precon_roles").delete().eq("id", id)); await loadRoles(); },
  };

  // The app keys permissions by a slug of the role's label; the database keys them by role id.
  handlers.permissions = {
    tables: ["precon_role_permissions", "precon_roles"],
    async list() {
      await loadRoles();
      const rows = check(await sb.from("precon_role_permissions").select("role_id,section,allowed")) || [];
      return roles.list.map(r => {
        const data = {};
        rows.filter(x => x.role_id === r.id).forEach(x => { data[x.section] = x.allowed; });
        return { id: roleSlug(r.label), data };
      });
    },
    async set(slug, d) { return handlers.permissions.update(slug, d); },
    async update(slug, p) {
      const role_id = await roleIdForSlug(slug);
      if (!role_id) return; // e.g. the old slug after a role rename: nothing left to write
      const rows = Object.keys(p).map(section => ({ role_id, section, allowed: !!p[section] }));
      if (rows.length) check(await sb.from("precon_role_permissions").upsert(rows, { onConflict: "role_id,section" }));
    },
    async remove(slug) {
      const role_id = await roleIdForSlug(slug);
      if (role_id) check(await sb.from("precon_role_permissions").delete().eq("role_id", role_id));
    },
  };

  handlers.users = {
    tables: ["precon_users", "precon_roles"],
    async list() {
      await loadRoles();
      const rows = check(await sb.from("precon_users").select("id,name,email,role_id").order("name")) || [];
      return rows.map(u => ({ id: u.id, data: { name: u.name, role: roleLabel(u.role_id), accountId: u.email || null } }));
    },
    async toRow(p) {
      const row = {};
      if ("name" in p) row.name = p.name;
      if ("role" in p) { row.role_id = await roleIdFor(p.role); if (!row.role_id) throw { code: "invalid_argument", message: "Unknown role" }; }
      if ("accountId" in p) row.email = p.accountId ? String(p.accountId).toLowerCase().trim() : null;
      return row;
    },
    async add(d) { const row = await handlers.users.toRow(d); return check(await sb.from("precon_users").insert(row).select("id").single()).id; },
    async set(id, d) { const row = await handlers.users.toRow(d); check(await sb.from("precon_users").upsert({ id, ...row })); },
    async update(id, p) { const row = await handlers.users.toRow(p); if (Object.keys(row).length) check(await sb.from("precon_users").update(row).eq("id", id)); },
    async remove(id) { check(await sb.from("precon_users").delete().eq("id", id)); },
  };

  handlers.statuses = {
    tables: ["precon_statuses"],
    async list() {
      const rows = check(await sb.from("precon_statuses").select("id,label,color,sort,system_key").order("sort")) || [];
      return rows.map(s => ({ id: s.id, data: { label: s.label, color: s.color, order: s.sort, systemKey: s.system_key } }));
    },
    async add(d) { const id = "st-" + rand(); await handlers.statuses.set(id, d); return id; },
    async set(id, d) { check(await sb.from("precon_statuses").upsert({ id, label: d.label, color: d.color, sort: d.order || 0 })); },
    async update(id, p) {
      const row = {};
      if ("label" in p) row.label = p.label;
      if ("color" in p) row.color = p.color;
      if ("order" in p) row.sort = p.order;
      if (Object.keys(row).length) check(await sb.from("precon_statuses").update(row).eq("id", id));
    },
    async remove(id) { check(await sb.from("precon_statuses").delete().eq("id", id)); },
  };

  function optionHandler(kind) {
    return {
      tables: ["precon_options"],
      async list() {
        const rows = check(await sb.from("precon_options").select("id,label,sort").eq("kind", kind).order("sort")) || [];
        return rows.map(o => ({ id: o.id, data: { label: o.label, order: o.sort } }));
      },
      async add(d) { const id = kind.slice(0, 3) + "-" + rand(); await this.set(id, d); return id; },
      async set(id, d) { check(await sb.from("precon_options").upsert({ id, kind, label: d.label, sort: d.order || 0 })); },
      async update(id, p) {
        const row = {};
        if ("label" in p) row.label = p.label;
        if ("order" in p) row.sort = p.order;
        if (Object.keys(row).length) check(await sb.from("precon_options").update(row).eq("id", id));
      },
      async remove(id) { check(await sb.from("precon_options").delete().eq("id", id)); },
    };
  }
  handlers.finance_options = optionHandler("finance");
  handlers.approval_options = optionHandler("approval");

  handlers.workflow_templates = {
    tables: ["precon_templates", "precon_template_stages", "precon_template_steps"],
    async list() {
      await loadRoles();
      const [t, st, sp] = await Promise.all([
        sb.from("precon_templates").select("id,type,name").order("name"),
        sb.from("precon_template_stages").select("template_id,id,name,sort").order("sort"),
        sb.from("precon_template_steps").select("template_id,id,sort,name,days,role_id,stage_id,reminder,is_handover,checklist_ids").order("sort"),
      ]);
      const tpl = check(t) || [], stages = check(st) || [], steps = check(sp) || [];
      return tpl.map(x => ({
        id: x.id,
        data: {
          type: x.type, name: x.name,
          stages: stages.filter(s => s.template_id === x.id).map(s => ({ id: s.id, name: s.name })),
          steps: steps.filter(s => s.template_id === x.id).map(s => ({ id: s.id, name: s.name, days: s.days, role: roleLabel(s.role_id), reminder: s.reminder, stageId: s.stage_id || null, isHandover: !!s.is_handover, checklistIds: s.checklist_ids || [] })),
        },
      }));
    },
    // Order matters: stages first (steps point at them), then steps, then remove what's gone.
    async writeParts(id, stages, steps) {
      if (stages) {
        const rows = stages.map((s, i) => ({ template_id: id, id: s.id, name: s.name, sort: i }));
        if (rows.length) check(await sb.from("precon_template_stages").upsert(rows, { onConflict: "template_id,id" }));
      }
      if (steps) {
        const rows = [];
        for (let i = 0; i < steps.length; i++) {
          const s = steps[i];
          const row = { template_id: id, id: s.id, sort: i, name: s.name, days: Number(s.days) || 1, role_id: await roleIdFor(s.role), stage_id: s.stageId || null, reminder: !!s.reminder };
          // Settings some screens don't carry (for example a spreadsheet import) are left as stored.
          if ("isHandover" in s) row.is_handover = !!s.isHandover;
          if (Array.isArray(s.checklistIds)) row.checklist_ids = s.checklistIds.filter(x => typeof x === "string" && x).slice(0, 20);
          rows.push(row);
        }
        // A bulk upsert needs the same columns in every row, so rows are sent in groups by shape.
        const groups = {};
        rows.forEach(r => { const k = Object.keys(r).sort().join(","); (groups[k] = groups[k] || []).push(r); });
        for (const k of Object.keys(groups)) check(await sb.from("precon_template_steps").upsert(groups[k], { onConflict: "template_id,id" }));
        let del = sb.from("precon_template_steps").delete().eq("template_id", id);
        if (rows.length) del = del.not("id", "in", inList(rows.map(r => r.id)));
        check(await del);
      }
      if (stages) {
        let del = sb.from("precon_template_stages").delete().eq("template_id", id);
        if (stages.length) del = del.not("id", "in", inList(stages.map(s => s.id)));
        check(await del);
      }
    },
    async add(d) {
      const id = check(await sb.from("precon_templates").insert({ type: d.type, name: d.name }).select("id").single()).id;
      await handlers.workflow_templates.writeParts(id, d.stages || [], d.steps || []);
      return id;
    },
    async set(id, d) {
      check(await sb.from("precon_templates").upsert({ id, type: d.type, name: d.name }));
      await handlers.workflow_templates.writeParts(id, d.stages || [], d.steps || []);
    },
    async update(id, p) {
      const row = {};
      if ("name" in p) row.name = p.name;
      if ("type" in p) row.type = p.type;
      if (Object.keys(row).length) check(await sb.from("precon_templates").update(row).eq("id", id));
      if ("stages" in p || "steps" in p) await handlers.workflow_templates.writeParts(id, p.stages || null, p.steps || null);
    },
    async remove(id) { check(await sb.from("precon_templates").delete().eq("id", id)); },
  };

  const JOB_FIELDS = {
    type: "type", templateId: "template_id", templateName: "template_name", clientName: "client_name", address: "address",
    investmentDate: "investment_date", investmentValue: "investment_value", investmentGP: "investment_gp", finance: "finance_id",
    approvals: "approvals_id", hubspotContactId: "hubspot_contact_id", archived: "archived", baselineMode: "baseline_mode",
    stages: "stages", createdBy: "created_by", state: "state", statusNote: "status_note",
  };
  const STEP_FIELDS = { status: "status_id", baseline: "baseline", forecast: "forecast", complete: "complete", assignedUser: "assigned_user", notes: "notes" };
  function jobRow(d) {
    const row = {};
    Object.keys(JOB_FIELDS).forEach(k => { if (k in d) row[JOB_FIELDS[k]] = d[k] === undefined ? null : d[k]; });
    if ("stages" in row) row.stages = row.stages || [];
    return row;
  }
  function stepPatch(p) {
    const row = {};
    Object.keys(STEP_FIELDS).forEach(k => { if (k in p) row[STEP_FIELDS[k]] = p[k] === undefined ? null : p[k]; });
    if ("notes" in row && row.notes == null) row.notes = "";
    return row;
  }

  handlers.jobs = {
    tables: ["precon_jobs", "precon_job_steps"],
    async list() {
      const [j, s] = await Promise.all([
        sb.from("precon_jobs").select("*").order("code"),
        sb.from("precon_job_steps").select("*").order("sort"),
      ]);
      const jobs = check(j) || [], steps = check(s) || [];
      const byJob = {};
      steps.forEach(x => { (byJob[x.job_code] = byJob[x.job_code] || []).push(x); });
      return jobs.map(x => {
        const js = (byJob[x.code] || []).sort((a, b) => a.sort - b.sort);
        const stepData = {};
        js.forEach(r => { stepData[r.step_id] = { status: r.status_id, baseline: r.baseline, forecast: r.forecast, complete: r.complete, assignedUser: r.assigned_user, notes: r.notes || "" }; });
        return {
          id: x.code,
          data: {
            type: x.type, templateId: x.template_id, templateName: x.template_name, clientName: x.client_name, address: x.address,
            investmentDate: x.investment_date, investmentValue: num(x.investment_value), investmentGP: num(x.investment_gp),
            finance: x.finance_id, approvals: x.approvals_id, hubspotContactId: x.hubspot_contact_id, archived: x.archived,
            baselineMode: x.baseline_mode, createdAt: x.created_at, createdBy: x.created_by, stages: x.stages || [],
            state: x.state || null, statusNote: x.status_note || "", statusNoteBy: x.status_note_by || null, statusNoteAt: x.status_note_at || null,
            stepDefs: js.map(r => ({ id: r.step_id, name: r.name, days: r.days, role: r.role_label, stageId: r.stage_id, reminder: r.reminder, isHandover: !!r.is_handover })),
            stepData,
          },
        };
      });
    },
    async set(code, d) {
      check(await sb.from("precon_jobs").upsert({ code, ...jobRow(d) }));
      const defs = d.stepDefs || [];
      const data = d.stepData || {};
      const rows = defs.map((def, i) => ({
        job_code: code, step_id: def.id, sort: i, name: def.name, days: Number(def.days) || 0, role_label: def.role || "",
        stage_id: def.stageId || null, reminder: !!def.reminder, is_handover: !!def.isHandover, ...stepPatch({ status: "not-started", notes: "", ...(data[def.id] || {}) }),
      }));
      if (rows.length) check(await sb.from("precon_job_steps").upsert(rows, { onConflict: "job_code,step_id" }));
    },
    async update(code, p) {
      const row = jobRow(p);
      // The status note has its own route so anyone working on the job can update it.
      if ("status_note" in row) {
        check(await sb.rpc("precon_set_job_status_note", { p_code: code, p_note: row.status_note || "" }));
        delete row.status_note;
      }
      if (Object.keys(row).length) check(await sb.from("precon_jobs").update(row).eq("code", code));
      // Each step is its own row, so saving one person's change never overwrites another's.
      if (p.stepData) {
        for (const stepId of Object.keys(p.stepData)) {
          const sp = stepPatch(p.stepData[stepId] || {});
          if (Object.keys(sp).length) check(await sb.from("precon_job_steps").update(sp).eq("job_code", code).eq("step_id", stepId));
        }
      }
    },
    async remove(code) { check(await sb.from("precon_jobs").delete().eq("code", code)); },
  };

  // History is append-only in the database. Entries the app already knows about are tracked so a
  // later full "set" only inserts what's new, and deletes are ignored (they cascade with the job).
  const seenHistory = {};
  handlers.job_history = {
    tables: [],
    async get(jobCode) {
      const rows = check(await sb.from("precon_job_history").select("*").eq("job_code", jobCode).order("at", { ascending: false }).limit(300)) || [];
      const entries = {};
      const seen = seenHistory[jobCode] = seenHistory[jobCode] || new Set();
      rows.forEach(r => {
        const key = "db-" + r.id;
        seen.add(key);
        entries[key] = { at: r.at, by: r.by_user, stepId: r.step_id, stepName: r.step_name, field: r.field, from: r.from_value, to: r.to_value, auto: r.auto };
      });
      return rows.length ? { entries } : null;
    },
    async insert(jobCode, entries) {
      const seen = seenHistory[jobCode] = seenHistory[jobCode] || new Set();
      const keys = Object.keys(entries || {}).filter(k => !seen.has(k) && !k.startsWith("db-"));
      const rows = keys.map(k => {
        const e = entries[k];
        return { job_code: jobCode, at: e.at, by_user: e.by || null, step_id: e.stepId || null, step_name: e.stepName || null, field: String(e.field || "").slice(0, 40), from_value: e.from == null ? null : String(e.from).slice(0, 2100), to_value: e.to == null ? null : String(e.to).slice(0, 2100), auto: !!e.auto };
      });
      if (rows.length) check(await sb.from("precon_job_history").insert(rows));
      keys.forEach(k => seen.add(k));
    },
    async set(jobCode, d) { await handlers.job_history.insert(jobCode, (d || {}).entries); },
    async update(jobCode, p) { await handlers.job_history.insert(jobCode, (p || {}).entries); },
    async remove() { /* removed with the job */ },
  };

  handlers.settings = {
    tables: ["precon_settings"],
    async list() {
      const rows = check(await sb.from("precon_settings").select("key,value")) || [];
      return rows.map(r => ({ id: r.key, data: { value: r.value } }));
    },
    async set(key, d) { check(await sb.from("precon_settings").upsert({ key, value: d.value === undefined ? {} : d.value })); },
    async update(key, p) { return handlers.settings.set(key, p); },
    async remove(key) { check(await sb.from("precon_settings").delete().eq("key", key)); },
  };
  handlers.user_prefs = {
    tables: [],
    async get(userId) {
      const rows = check(await sb.from("precon_user_prefs").select("dashboard").eq("user_id", userId)) || [];
      return rows.length ? { dashboard: rows[0].dashboard || {} } : null;
    },
    async set(userId, d) { check(await sb.from("precon_user_prefs").upsert({ user_id: userId, dashboard: (d && d.dashboard) || {} })); },
    async update(userId, p) { return handlers.user_prefs.set(userId, p); },
    async remove(userId) { check(await sb.from("precon_user_prefs").delete().eq("user_id", userId)); },
  };

  const SECTIONS_WITH_CHECKLISTS = ["customerservice", "drafting", "approvals", "estimating"];
  const cleanOne = (it, i, prefix) => ({ id: String(it.id || prefix + (i + 1)).slice(0, 60), label: String(it.label || "").trim().slice(0, 200) });
  // Template items, each with optional sub-items ("children", one level).
  const cleanItems = items => (Array.isArray(items) ? items : []).map((it, i) => {
    const o = cleanOne(it, i, "i");
    const ch = (Array.isArray(it.children) ? it.children : []).map((c, k) => cleanOne(c, k, o.id + "-")).filter(c => c.label).slice(0, 50);
    if (ch.length) o.children = ch;
    return o;
  }).filter(it => it.label).slice(0, 100);
  handlers.checklist_templates = {
    tables: ["precon_checklist_templates"],
    async list() {
      const rows = check(await sb.from("precon_checklist_templates").select("id,section,name,items").order("name")) || [];
      return rows.map(r => ({ id: r.id, data: { section: r.section, name: r.name, items: cleanItems(r.items) } }));
    },
    row(d) {
      const row = {};
      if ("section" in d) { if (!SECTIONS_WITH_CHECKLISTS.includes(d.section)) throw { code: "invalid_argument", message: "Unknown section" }; row.section = d.section; }
      if ("name" in d) row.name = String(d.name || "").trim().slice(0, 80);
      if ("items" in d) row.items = cleanItems(d.items);
      return row;
    },
    async add(d) { return check(await sb.from("precon_checklist_templates").insert(handlers.checklist_templates.row(d)).select("id").single()).id; },
    async set(id, d) { check(await sb.from("precon_checklist_templates").upsert({ id, ...handlers.checklist_templates.row(d) })); },
    async update(id, p) { const row = handlers.checklist_templates.row(p); if (Object.keys(row).length) check(await sb.from("precon_checklist_templates").update(row).eq("id", id)); },
    async remove(id) { check(await sb.from("precon_checklist_templates").delete().eq("id", id)); },
  };

  // Job checklists: each item is its own row, so ticks by different people never overwrite each other.
  handlers.job_checklists = {
    tables: ["precon_job_checklists", "precon_job_checklist_items"],
    async list() {
      const [c, i] = await Promise.all([
        sb.from("precon_job_checklists").select("*").order("created_at"),
        sb.from("precon_job_checklist_items").select("*").order("sort"),
      ]);
      const lists = check(c) || [], items = check(i) || [];
      const byList = {};
      items.forEach(x => { (byList[x.checklist_id] = byList[x.checklist_id] || []).push(x); });
      return lists.map(x => ({
        id: x.id,
        data: {
          jobCode: x.job_code, section: x.section, name: x.name, stepId: x.step_id || null, templateId: x.template_id || null,
          status: x.status, createdAt: x.created_at, createdBy: x.created_by, closedAt: x.closed_at, closedBy: x.closed_by, updatedAt: x.updated_at,
          items: (byList[x.id] || []).sort((a, b) => a.sort - b.sort).map(r => ({ id: r.id, parentId: r.parent_id || null, label: r.label, done: !!r.done, doneBy: r.done_by, doneAt: r.done_at, note: r.note || "" })),
        },
      }));
    },
    async add(d) {
      if (!SECTIONS_WITH_CHECKLISTS.includes(d.section)) throw { code: "invalid_argument", message: "Unknown section" };
      const id = check(await sb.from("precon_job_checklists").insert({
        job_code: d.jobCode, section: d.section, name: String(d.name || "").trim().slice(0, 80), step_id: d.stepId || null, template_id: d.templateId || null,
      }).select("id").single()).id;
      const tops = cleanItems(d.items);
      const items = tops.map((it, n) => ({ checklist_id: id, id: it.id, sort: n, label: it.label }));
      const subs = [];
      tops.forEach(it => (it.children || []).forEach((c, k) => subs.push({ checklist_id: id, id: c.id, parent_id: it.id, sort: k + 1, label: c.label })));
      if (items.length) check(await sb.from("precon_job_checklist_items").insert(items));
      if (subs.length) check(await sb.from("precon_job_checklist_items").insert(subs));
      return id;
    },
    async update(id, p) {
      const row = {};
      if ("name" in p) row.name = String(p.name || "").trim().slice(0, 80);
      if ("status" in p) row.status = p.status === "closed" ? "closed" : "open";
      if (Object.keys(row).length) check(await sb.from("precon_job_checklists").update(row).eq("id", id));
      for (const itemId of Object.keys(p.itemUpdates || {})) {
        const u = p.itemUpdates[itemId] || {};
        const ir = {};
        if ("done" in u) ir.done = !!u.done;
        if ("note" in u) ir.note = String(u.note || "").slice(0, 500);
        if ("label" in u) ir.label = String(u.label || "").trim().slice(0, 200);
        if ("sort" in u) ir.sort = Number(u.sort) || 0;
        if (Object.keys(ir).length) check(await sb.from("precon_job_checklist_items").update(ir).eq("checklist_id", id).eq("id", itemId));
      }
      // New items: top-level first, then sub-items (which point at their parent).
      const add = (p.addItems || []).map((it, n) => ({ ...cleanOne(it, n, "i"), sort: Number(it.sort) || 1000 + n, parentId: it.parentId ? String(it.parentId).slice(0, 60) : null })).filter(it => it.label);
      const addRow = it => ({ checklist_id: id, id: it.id, sort: it.sort, label: it.label, ...(it.parentId ? { parent_id: it.parentId } : {}) });
      const addTop = add.filter(it => !it.parentId).map(addRow), addSub = add.filter(it => it.parentId).map(addRow);
      if (addTop.length) check(await sb.from("precon_job_checklist_items").insert(addTop));
      if (addSub.length) check(await sb.from("precon_job_checklist_items").insert(addSub));
      for (const itemId of (p.removeItems || [])) {
        check(await sb.from("precon_job_checklist_items").delete().eq("checklist_id", id).eq("parent_id", itemId));
        check(await sb.from("precon_job_checklist_items").delete().eq("checklist_id", id).eq("id", itemId));
      }
    },
    async remove(id) { check(await sb.from("precon_job_checklists").delete().eq("id", id)); },
  };

  // Drawing change register (Drafting). Numbering, who and when are set by the database.
  const CHANGE_FIELDS = {
    raisedOn: "raised_on", source: "source", requestedBy: "requested_by", description: "description", drawings: "drawings",
    revision: "revision", assignedTo: "assigned_to", dueOn: "due_on", costImpact: "cost_impact", timeImpactDays: "time_impact_days",
    approvalRequired: "approval_required", approvedOn: "approved_on", status: "status", completedOn: "completed_on", notes: "notes",
  };
  const CHANGE_TEXT = { requested_by: 120, description: 1000, drawings: 200, revision: 40, notes: 2000 };
  function changeRow(d) {
    const row = {};
    Object.keys(CHANGE_FIELDS).forEach(k => {
      if (!(k in d)) return;
      const col = CHANGE_FIELDS[k];
      let v = d[k];
      if (col in CHANGE_TEXT) v = String(v == null ? "" : v).slice(0, CHANGE_TEXT[col]);
      else if (col === "cost_impact" || col === "time_impact_days") v = v === "" || v == null || isNaN(Number(v)) ? null : (col === "time_impact_days" ? Math.round(Number(v)) : Number(v));
      else if (col === "approval_required") v = !!v;
      else if (v === "" || v === undefined) v = null;
      row[col] = v;
    });
    return row;
  }
  handlers.job_changes = {
    tables: ["precon_job_changes"],
    async list() {
      const rows = check(await sb.from("precon_job_changes").select("*").order("job_code").order("number")) || [];
      return rows.map(r => ({
        id: r.id,
        data: {
          jobCode: r.job_code, number: r.number, raisedOn: r.raised_on, source: r.source, requestedBy: r.requested_by || "",
          description: r.description, drawings: r.drawings || "", revision: r.revision || "", assignedTo: r.assigned_to || null,
          dueOn: r.due_on || null, costImpact: num(r.cost_impact), timeImpactDays: r.time_impact_days == null ? null : Number(r.time_impact_days),
          approvalRequired: !!r.approval_required, approvedOn: r.approved_on || null, status: r.status, completedOn: r.completed_on || null,
          notes: r.notes || "", createdAt: r.created_at, createdBy: r.created_by, updatedAt: r.updated_at, updatedBy: r.updated_by,
        },
      }));
    },
    async add(d) {
      const row = { job_code: d.jobCode, ...changeRow(d) };
      return check(await sb.from("precon_job_changes").insert(row).select("id").single()).id;
    },
    async update(id, p) { const row = changeRow(p); if (Object.keys(row).length) check(await sb.from("precon_job_changes").update(row).eq("id", id)); },
    async remove(id) { check(await sb.from("precon_job_changes").delete().eq("id", id)); },
  };

  // Supplier quotes on a job (Estimating): the register; the file itself lives in the job's SharePoint folder.
  const QUOTE_FIELDS = { supplier: "supplier", trade: "trade", description: "description", amountExGst: "amount_ex_gst", receivedOn: "received_on",
    validUntil: "valid_until", status: "status", notes: "notes", itemId: "item_id", fileName: "file_name", webUrl: "web_url" };
  const QUOTE_TEXT = { supplier: 120, trade: 80, description: 500, notes: 2000, file_name: 400, web_url: 2000 };
  function quoteRow(d) {
    const row = {};
    Object.keys(QUOTE_FIELDS).forEach(k => {
      if (!(k in d)) return;
      const col = QUOTE_FIELDS[k];
      let v = d[k];
      if (col in QUOTE_TEXT) v = String(v == null ? "" : v).trim().slice(0, QUOTE_TEXT[col]);
      else if (col === "amount_ex_gst") v = v === "" || v == null || isNaN(Number(v)) ? null : Math.round(Number(v) * 100) / 100;
      else if (col === "status") v = ["received", "accepted", "declined", "superseded"].includes(v) ? v : "received";
      else if (v === "" || v === undefined) v = null;
      row[col] = v;
    });
    return row;
  }
  handlers.job_quotes = {
    tables: ["precon_job_quotes"],
    async list() {
      const rows = check(await sb.from("precon_job_quotes").select("*").order("job_code").order("received_on", { ascending: false })) || [];
      return rows.map(r => ({ id: r.id, data: {
        jobCode: r.job_code, supplier: r.supplier, trade: r.trade || "", description: r.description || "", amountExGst: num(r.amount_ex_gst),
        receivedOn: r.received_on || null, validUntil: r.valid_until || null, status: r.status, decidedOn: r.decided_on || null, notes: r.notes || "",
        itemId: r.item_id || null, fileName: r.file_name || "", webUrl: r.web_url || "",
        createdAt: r.created_at, createdBy: r.created_by, updatedAt: r.updated_at, updatedBy: r.updated_by,
      } }));
    },
    async add(d) { return check(await sb.from("precon_job_quotes").insert({ job_code: d.jobCode, ...quoteRow(d) }).select("id").single()).id; },
    async update(id, p) { const row = quoteRow(p); if (Object.keys(row).length) check(await sb.from("precon_job_quotes").update(row).eq("id", id)); },
    async remove(id) { check(await sb.from("precon_job_quotes").delete().eq("id", id)); },
  };

  // Approvals forms. Site, title and owner details per job (anyone who can see the job edits them), the form templates
  // (administrators edit the settings; the files themselves go through the approval-forms server function) and the log
  // of forms filled for each job (written by that function).
  const SITE_TEXT = { lot: 40, plan: 40, lga: 120, zoning: 80, landUse: 80, ownerName: 200, ownerPhone: 40, ownerMobile: 40, ownerEmail: 254,
    ownerAddress: 200, ownerSuburb: 80, ownerState: 10, ownerPostcode: 10, treatmentBrand: 80, treatmentModel: 80, treatmentTpa: 60, treatmentFlow: 20, notes: 1000 };
  const snake = k => k.replace(/[A-Z]/g, c => "_" + c.toLowerCase());
  function siteRow(d) {
    const row = {};
    Object.keys(SITE_TEXT).forEach(k => { if (k in d) row[snake(k)] = String(d[k] == null ? "" : d[k]).trim().slice(0, SITE_TEXT[k]); });
    if ("siteArea" in d) row.site_area_m2 = d.siteArea === "" || d.siteArea == null || isNaN(Number(d.siteArea)) || Number(d.siteArea) < 0 ? null : Math.round(Number(d.siteArea) * 100) / 100;
    if ("soilClass" in d) row.soil_class = ["A", "S", "M", "H1", "H2", "E", "P"].includes(d.soilClass) ? d.soilClass : "";
    if ("sewerage" in d) row.sewerage = ["sewered", "unsewered"].includes(d.sewerage) ? d.sewerage : "";
    return row;
  }
  handlers.job_site = {
    tables: ["precon_job_site"],
    async list() {
      const rows = check(await sb.from("precon_job_site").select("*").order("job_code")) || [];
      return rows.map(r => {
        const data = { siteArea: num(r.site_area_m2), soilClass: r.soil_class || "", sewerage: r.sewerage || "", updatedAt: r.updated_at, updatedBy: r.updated_by };
        Object.keys(SITE_TEXT).forEach(k => { data[k] = r[snake(k)] || ""; });
        return { id: r.job_code, data };
      });
    },
    async set(code, d) { check(await sb.from("precon_job_site").upsert({ job_code: code, ...siteRow(d) }, { onConflict: "job_code" })); },
    async update(code, p) { return handlers.job_site.set(code, p); },
    async remove(code) { check(await sb.from("precon_job_site").delete().eq("job_code", code)); },
  };
  const cleanMapping = m => (Array.isArray(m) ? m : []).slice(0, 400).map(x => {
    const o = { target: String((x && x.target) || "").slice(0, 200), value: String((x && x.value) == null ? "" : x.value).slice(0, 500) };
    if (x && x.kind === "check") o.kind = "check";
    ["page", "x", "y", "size", "width"].forEach(k => { if (x && x[k] !== undefined && x[k] !== "" && !isNaN(Number(x[k]))) o[k] = Math.round(Number(x[k]) * 10) / 10; });
    return o;
  }).filter(x => x.target);
  const cleanDefaults = d => {
    const out = {};
    if (d && typeof d === "object" && !Array.isArray(d)) Object.keys(d).slice(0, 200).forEach(k => { if (/^[A-Za-z0-9_.]{1,60}$/.test(k)) out[k] = String(d[k] == null ? "" : d[k]).slice(0, 500); });
    return out;
  };
  handlers.form_templates = {
    tables: ["precon_form_templates"],
    async list() {
      const rows = check(await sb.from("precon_form_templates").select("*").order("sort").order("name")) || [];
      return rows.map(r => ({ id: r.id, data: {
        name: r.name, kind: r.kind, appliesTo: r.applies_to, fileName: r.file_name || "", fileSize: r.file_size || null, folder: r.folder || "05_COUNCIL",
        fields: r.fields || [], mapping: r.mapping || [], defaults: r.defaults || {}, active: !!r.active, sort: r.sort || 0, updatedAt: r.updated_at, updatedBy: r.updated_by,
      } }));
    },
    async add() { throw { code: "invalid_argument", message: "Form templates are added by uploading the form" }; },
    async update(id, p) {
      const row = {};
      if ("name" in p) row.name = String(p.name || "").trim().slice(0, 100);
      if ("appliesTo" in p) row.applies_to = ["residential", "commercial", "both"].includes(p.appliesTo) ? p.appliesTo : "both";
      if ("folder" in p) row.folder = String(p.folder || "").trim().slice(0, 80) || "05_COUNCIL";
      if ("mapping" in p) row.mapping = cleanMapping(p.mapping);
      if ("defaults" in p) row.defaults = cleanDefaults(p.defaults);
      if ("active" in p) row.active = !!p.active;
      if ("sort" in p) row.sort = Math.round(Number(p.sort) || 0);
      if (Object.keys(row).length) check(await sb.from("precon_form_templates").update(row).eq("id", id));
    },
    async remove(id) { check(await sb.from("precon_form_templates").delete().eq("id", id)); },
  };
  handlers.job_forms = {
    tables: ["precon_job_forms"],
    async list() {
      const rows = check(await sb.from("precon_job_forms").select("*").order("created_at", { ascending: false }).limit(2000)) || [];
      return rows.map(r => ({ id: r.id, data: {
        jobCode: r.job_code, templateId: r.template_id || null, templateName: r.template_name || "", fileName: r.file_name || "", itemId: r.item_id || null,
        webUrl: r.web_url || "", savedTo: r.saved_to, values: r.values || {}, createdAt: r.created_at, createdBy: r.created_by,
      } }));
    },
    async add() { throw { code: "invalid_argument", message: "Forms are filled through the approval-forms service" }; },
    async update() { throw { code: "invalid_argument", message: "Filled forms can't be changed" }; },
    async remove(id) { check(await sb.from("precon_job_forms").delete().eq("id", id)); },
  };

  // Contract templates (administrators edit them) and contracts with their timelines (read-only here:
  // the PandaDoc server function is the only thing that writes contracts).
  const CT_FIELDS = { name: "name", appliesTo: "applies_to", pandadocTemplateId: "pandadoc_template_id", pandadocTemplateName: "pandadoc_template_name", roles: "roles", fields: "fields", emailSubject: "email_subject", emailMessage: "email_message", active: "active", usesEstimate: "uses_estimate" };
  function contractTemplateRow(d) {
    const row = {};
    Object.keys(CT_FIELDS).forEach(k => { if (k in d) row[CT_FIELDS[k]] = d[k]; });
    if ("name" in row) row.name = String(row.name || "").trim().slice(0, 100);
    if ("applies_to" in row && !["residential", "commercial", "both"].includes(row.applies_to)) row.applies_to = "both";
    if ("roles" in row) row.roles = (Array.isArray(row.roles) ? row.roles : []).slice(0, 10).map((r, i) => ({ role: String(r.role || "").slice(0, 80), party: ["client", "client2", "westbuilt", "other"].includes(r.party) ? r.party : "other", name: String(r.name || "").slice(0, 120), email: String(r.email || "").trim().toLowerCase().slice(0, 254), order: Number(r.order) || i + 1 })).filter(r => r.role);
    if ("fields" in row) row.fields = (Array.isArray(row.fields) ? row.fields : []).slice(0, 30).map(f => ({ token: String(f.token || "").replace(/[^\w.]/g, "").slice(0, 60), label: String(f.label || "").slice(0, 80), default: String(f.default || "").slice(0, 200) })).filter(f => f.token);
    if ("email_subject" in row) row.email_subject = String(row.email_subject || "").slice(0, 200);
    if ("email_message" in row) row.email_message = String(row.email_message || "").slice(0, 2000);
    if ("active" in row) row.active = !!row.active;
    if ("uses_estimate" in row) row.uses_estimate = !!row.uses_estimate;
    return row;
  }
  handlers.contract_templates = {
    tables: ["precon_contract_templates"],
    async list() {
      const rows = check(await sb.from("precon_contract_templates").select("*").order("name")) || [];
      return rows.map(r => ({ id: r.id, data: { name: r.name, appliesTo: r.applies_to, pandadocTemplateId: r.pandadoc_template_id, pandadocTemplateName: r.pandadoc_template_name || "", roles: r.roles || [], fields: r.fields || [], emailSubject: r.email_subject || "", emailMessage: r.email_message || "", active: !!r.active, usesEstimate: !!r.uses_estimate } }));
    },
    async add(d) { return check(await sb.from("precon_contract_templates").insert(contractTemplateRow(d)).select("id").single()).id; },
    async update(id, p) { const row = contractTemplateRow(p); if (Object.keys(row).length) check(await sb.from("precon_contract_templates").update(row).eq("id", id)); },
    async remove(id) { check(await sb.from("precon_contract_templates").delete().eq("id", id)); },
  };
  handlers.contracts = {
    tables: ["precon_contracts", "precon_contract_events"],
    async list() {
      const [c, e] = await Promise.all([
        sb.from("precon_contracts").select("*").order("job_code").order("version"),
        sb.from("precon_contract_events").select("*").order("at"),
      ]);
      const rows = check(c) || [], events = check(e) || [];
      const byContract = {};
      events.forEach(x => { (byContract[x.contract_id] = byContract[x.contract_id] || []).push({ at: x.at, status: x.status, source: x.source, by: x.by_user, detail: x.detail || "" }); });
      return rows.map(r => ({ id: r.id, data: {
        jobCode: r.job_code, templateId: r.template_id || null, series: r.series, templateName: r.template_name || "", title: r.title, version: r.version,
        pandadocId: r.pandadoc_id || null, status: r.status, supersededBy: r.superseded_by || null, figures: r.figures || {}, recipients: r.recipients || [],
        estimateId: r.estimate_id || null, estimateSnapshot: r.estimate_snapshot || null,
        lastError: r.last_error || "", createdAt: r.created_at, createdBy: r.created_by, sentAt: r.sent_at, sentBy: r.sent_by, completedAt: r.completed_at, statusAt: r.status_at,
        events: byContract[r.id] || [],
      } }));
    },
    async add() { throw { code: "invalid_argument", message: "Contracts are created through PandaDoc" }; },
    async update() { throw { code: "invalid_argument", message: "Contracts are updated through PandaDoc" }; },
    async remove() { throw { code: "invalid_argument", message: "Contracts can't be deleted" }; },
  };

  // ---------------------------------------------------------------- document-store facade
  const snap = (id, data) => ({ id, exists: data != null, data: () => (data == null ? undefined : JSON.parse(JSON.stringify(data))) });
  function handlerFor(name) {
    const h = handlers[name];
    if (!h) throw { code: "bad_request", message: "Unknown collection " + name };
    return h;
  }
  function docRef(name, id) {
    const h = handlerFor(name);
    return {
      id,
      async get() {
        if (h.get) return snap(id, await h.get(id));
        const found = (await h.list()).find(x => x.id === id);
        return snap(id, found ? found.data : null);
      },
      set: (d) => h.set(id, d),
      update: (p) => h.update(id, p),
      delete: () => h.remove(id),
    };
  }
  function collection(name) {
    const h = handlerFor(name);
    return {
      doc: (id) => docRef(name, id),
      async add(d) { const id = await h.add(d); return docRef(name, id); },
      async get() { const docs = (await h.list()).map(x => snap(x.id, x.data)); return { docs, size: docs.length, empty: !docs.length }; },
      onSnapshot(cb, onError) {
        if (!h.tables.length || typeof sb.channel !== "function") return () => {};
        let timer = null;
        const refresh = () => {
          clearTimeout(timer);
          timer = setTimeout(async () => {
            try { const docs = (await h.list()).map(x => snap(x.id, x.data)); cb({ docs }); }
            catch (e) { if (onError) onError(e); }
          }, 350);
        };
        const ch = sb.channel("precon-" + name + "-" + rand());
        h.tables.forEach(table => ch.on("postgres_changes", { event: "*", schema: "public", table }, refresh));
        ch.subscribe();
        return () => { clearTimeout(timer); sb.removeChannel(ch); };
      },
    };
  }
  // Named database functions the add-ons call (each checks access itself).
  const RPCS = ["precon_add_step_checklists"];
  async function rpc(name, args) {
    if (!RPCS.includes(name)) throw { code: "bad_request", message: "Unknown function" };
    return check(await sb.rpc(name, args || {}));
  }
  return { collection, rpc, doc: (path) => { const [c, id] = String(path).split("/"); return docRef(c, id); }, _handlers: handlers };
}
